import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = anonymous, object = signed in
    const [user, setUser] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        // Skip /me if we're mid-Google-callback (handled by AuthCallback route)
        if (window.location.hash?.includes("session_id=")) {
            setUser(false);
            return;
        }
        refresh();
    }, [refresh]);

    async function login(email, password) {
        const { data } = await api.post("/auth/login", { email, password });
        if (data?.access_token) localStorage.setItem("srtf_access_token", data.access_token);
        setUser(data.user);
        return data.user;
    }

    async function register(email, password, name) {
        const { data } = await api.post("/auth/register", { email, password, name });
        if (data?.access_token) localStorage.setItem("srtf_access_token", data.access_token);
        setUser(data.user);
        return data.user;
    }

    async function logout() {
        try {
            await api.post("/auth/logout");
        } catch {}
        localStorage.removeItem("srtf_access_token");
        setUser(false);
    }

    async function updateProfile(patch) {
        const { data } = await api.patch("/auth/me", patch);
        setUser(data);
        return data;
    }

    return (
        <AuthContext.Provider
            value={{ user, setUser, login, register, logout, refresh, updateProfile, formatApiError }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
